import fileDownload from 'js-file-download';
import { BOTMARK_INVOICE_ITEM_NAME, hasEstimateReferencePlan } from './buildBotmarkEstimateInvoice';
import { estimateStatusValueGetter } from './columnValueGetters';
import { rowsToCsv } from './csvFormat';
import { formatMoney } from './moneyFormat';

export const BOTMARK_INVOICE_CSV_MIME = 'text/csv;charset=utf-8;';

export const POINT_TYPE_ENUM = {
    Under1000: 0,
    E: 1,
    C: 2,
    U: 3,
    Cl: 4,
};

export const BOTMARK_INVOICE_EXPORT_HEADERS = [
    'Date',
    'Invoice Number',
    'Company',
    'Customer',
    'Plan',
    'Status',
    'Under 1000',
    'E',
    'C',
    'U',
    'ECU Total',
    'ECU Rate',
    'CL Total',
    'CL Rate',
    'Base Service Fee',
    'Credit Amount',
    'Credited Plan',
    'Credit Description',
    'Sale Total',
];

function parseJsonValue(value, fallback) {
    if (!value) {
        return fallback;
    }

    try {
        return JSON.parse(value);
    } catch {
        return fallback;
    }
}

function findInvoiceItem(items, itemName) {
    if (!items) {
        return undefined;
    }

    return items.find((item) => item?.itemName === itemName);
}

function sumItemTotals(items) {
    if (!items) {
        return 0;
    }

    return items.reduce((sum, item) => sum + Number(item?.total ?? 0), 0);
}

function sumPointQuantities(pointList) {
    return (pointList ?? []).reduce((q, point) => {
        const quantity = Number(point?.Quantity ?? 0);

        switch (point?.TypeEnum) {
            case POINT_TYPE_ENUM.Under1000:
                q.under1000 += quantity;
                break;
            case POINT_TYPE_ENUM.E:
                q.e += quantity;
                q.ecu += quantity;
                break;
            case POINT_TYPE_ENUM.C:
                q.c += quantity;
                q.ecu += quantity;
                break;
            case POINT_TYPE_ENUM.U:
                q.u += quantity;
                q.ecu += quantity;
                break;
            case POINT_TYPE_ENUM.Cl:
                q.cl += quantity;
                break;
        }

        return q;
    }, {
        under1000: 0, e: 0, c: 0, u: 0, cl: 0, ecu: 0
    });
}

function moneyOrEmpty(value) {
    if (value == null || value === '' || Number(value) === 0) {
        return '';
    }

    return formatMoney(value);
}

function rateOrEmpty(item) {
    if (item?.rate == null || item.rate === '') {
        return '';
    }

    return formatMoney(item.rate);
}

function buildInvoiceExportRow(invoice) {
    const invoiceValues = parseJsonValue(invoice?.invoiceValues, {});
    const items = invoiceValues?.items ?? [];
    const taxes = invoiceValues?.taxes ?? [];
    const pointQuantities = sumPointQuantities(parseJsonValue(invoice?.pointList, []));
    const bmPointsItem = findInvoiceItem(items, BOTMARK_INVOICE_ITEM_NAME.BmPoints);
    const clPointsItem = findInvoiceItem(items, BOTMARK_INVOICE_ITEM_NAME.ClPoints);
    const baseFeeItem = findInvoiceItem(items, BOTMARK_INVOICE_ITEM_NAME.BaseServiceFee);
    const creditItem = findInvoiceItem(items, BOTMARK_INVOICE_ITEM_NAME.Credit);
    const relatedFile = invoiceValues?.relatedFile;
    const hasCreditedPlan = hasEstimateReferencePlan(relatedFile);
    const creditAdjustment = Number(invoiceValues?.creditAdjustment ?? 0);
    const creditAmount = creditAdjustment || Math.abs(Number(creditItem?.total ?? 0));
    const saleTotal = sumItemTotals(items) + sumItemTotals(taxes);

    return {
        date: invoiceValues?.date ?? '',
        invoiceNumber: invoiceValues?.estimateNumber ?? '',
        company: invoiceValues?.processorInfo?.name ?? invoice?.developerName ?? '',
        customer: invoiceValues?.customerInfo?.userCompany ?? invoice?.customerName ?? '',
        plan: invoice?.fileName ?? '',
        status: estimateStatusValueGetter(undefined, invoice),
        under1000: pointQuantities.under1000,
        e: pointQuantities.e,
        c: pointQuantities.c,
        u: pointQuantities.u,
        ecu: invoiceValues?.pointTypeTotal ?? pointQuantities.ecu,
        ecuRate: rateOrEmpty(bmPointsItem),
        cl: pointQuantities.cl,
        clRate: rateOrEmpty(clPointsItem),
        baseServiceFee: moneyOrEmpty(baseFeeItem?.total),
        creditAmount: moneyOrEmpty(creditAmount),
        creditedPlan: hasCreditedPlan ? (relatedFile.fileName ?? '') : '',
        creditDescription: creditItem?.description ?? '',
        saleTotal: formatMoney(saleTotal),
        numericCreditAmount: creditAmount,
        numericSaleTotal: saleTotal,
        numericEcu: Number(invoiceValues?.pointTypeTotal ?? pointQuantities.ecu),
        numericUnder1000: pointQuantities.under1000,
        numericE: pointQuantities.e,
        numericC: pointQuantities.c,
        numericU: pointQuantities.u,
        numericCl: pointQuantities.cl,
    };
}

function toCsvCells(row) {
    return [
        row.date,
        row.invoiceNumber,
        row.company,
        row.customer,
        row.plan,
        row.status,
        row.under1000,
        row.e,
        row.c,
        row.u,
        row.ecu,
        row.ecuRate,
        row.cl,
        row.clRate,
        row.baseServiceFee,
        row.creditAmount,
        row.creditedPlan,
        row.creditDescription,
        row.saleTotal,
    ];
}

export function buildBotmarkInvoiceExportRows(invoices, { startDateLabel, endDateLabel } = {}) {
    const rows = [BOTMARK_INVOICE_EXPORT_HEADERS];
    const totals = {
        under1000: 0,
        e: 0,
        c: 0,
        u: 0,
        ecu: 0,
        cl: 0,
        creditAmount: 0,
        saleTotal: 0,
    };

    const invoiceList = invoices ?? [];
    for (let i = 0; i < invoiceList.length; i++) {
        const row = buildInvoiceExportRow(invoiceList[i]);
        totals.under1000 += row.numericUnder1000;
        totals.e += row.numericE;
        totals.c += row.numericC;
        totals.u += row.numericU;
        totals.ecu += row.numericEcu;
        totals.cl += row.numericCl;
        totals.creditAmount += row.numericCreditAmount;
        totals.saleTotal += row.numericSaleTotal;
        rows.push(toCsvCells(row));
    }

    if (startDateLabel == null && endDateLabel == null) {
        return rows;
    }

    rows.push([
        `${startDateLabel ?? ''} - ${endDateLabel ?? ''}`,
        '',
        '',
        '',
        'Totals:',
        '',
        totals.under1000,
        totals.e,
        totals.c,
        totals.u,
        totals.ecu,
        '',
        totals.cl,
        '',
        '',
        moneyOrEmpty(totals.creditAmount),
        '',
        '',
        formatMoney(totals.saleTotal),
    ]);

    return rows;
}

export function downloadBotmarkInvoiceCsv(invoices, fileName, options) {
    fileDownload(rowsToCsv(buildBotmarkInvoiceExportRows(invoices, options)), fileName, BOTMARK_INVOICE_CSV_MIME);
}
